import { VehicleDriverAccess } from "./VehicleDriverAccess";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "flowbite-react";
import { HiCheck } from "react-icons/hi";
import { FaRegCopy, FaExternalLinkAlt } from "react-icons/fa";
import { SimpleList, NorwayRectangle, StatensVegvesen } from "@components";
import { Company, Vehicle } from "../../models";
import { DetailHeader, Inline, Text } from "@design-system";

interface VehicleDetailsProps {
  vehicle: Vehicle | undefined;
  company: Company | undefined;
}

export const VehicleDetails: FC<VehicleDetailsProps> = ({
  vehicle,
  company,
}) => {
  const { t } = useTranslation(["common", "vehicles", "driver", "inventory"]);
  const [hasCopied, setHasCopied] = useState(false);
  // If no vehicle is selected, show a placeholder
  if (!vehicle) {
    return (
      <div className="p-4 text-gray-600 dark:text-gray-300">
        {t("vehicles:split.no_vehicle_selected")}
      </div>
    );
  }

  // Helper function: force uppercase and add a space after the first two characters
  const formatPlateNumber = (plate: string): string => {
    const upperPlate = plate.toUpperCase();
    return upperPlate.length > 2
      ? upperPlate.slice(0, 2) + " " + upperPlate.slice(2)
      : upperPlate;
  };

  const plateNumber =
    vehicle.registrationNumber !== ""
      ? formatPlateNumber(vehicle.registrationNumber)
      : formatPlateNumber(t("vehicles:unknown_vehicle"));

  const registrationForUrl =
    vehicle.registrationNumber !== ""
      ? vehicle.registrationNumber.replace(/\s/g, "")
      : "";

  return (
    <>
      {/* Vehicle Header */}
      <DetailHeader>
        <div className="flex items-center bg-surface border border-border rounded overflow-hidden">
          {/* Blue side panel with the Norwegian flag and "N" */}
          <div
            className="flex flex-col items-center justify-center bg-blue-700 p-1"
            style={{ width: "50px", height: "50px" }}
          >
            <NorwayRectangle />
            <span className="text-white text-xs font-bold">N</span>
          </div>

          {/* Main plate area with the registration number and copy functionality */}
          <Inline gap={4} className="p-2">
            <Text as="p" variant="detail-title">
              {plateNumber}
            </Text>

            {!hasCopied ? (
              <Tooltip content={t("common:copy")}>
                <div
                  className="relative rounded-md"
                  style={{ width: "34px", height: "34px" }}
                >
                  <FaRegCopy
                    className="w-6 h-6 dark:text-white cursor-pointer absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        vehicle.name ?? t("vehicles:unknown_vehicle"),
                      );
                      setHasCopied(true);
                      setTimeout(() => setHasCopied(false), 2000); // Reset after 2 seconds
                    }}
                  />
                </div>
              </Tooltip>
            ) : (
              <div className="flex items-center text-muted dark:text-text">
                <HiCheck className="mr-2 h-5 w-5" />
                <p>{t("common:copied")}</p>
              </div>
            )}
          </Inline>
        </div>
        {/* Button linking to the external "Kjøretøyopplysninger" URL */}
        <a
          href={`https://www.vegvesen.no/kjoretoy/kjop-og-salg/kjoretoyopplysninger/sjekk-kjoretoyopplysninger/?registreringsnummer=${registrationForUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto flex items-center gap-2 bg-surface-muted text-lgb-background-blue dark:text-white px-3 py-2 rounded border-solid border border-border-subtle transition transform duration-200 ease-in-out hover:shadow-lg active:scale-95"
        >
          <StatensVegvesen />
          <span>Kjøretøyopplysninger</span>
          <FaExternalLinkAlt className="w-4 h-4" />
        </a>
      </DetailHeader>

      {/* Basic Info */}
      <SimpleList
        items={[
          {
            title: t("inventory:vehicle_max_weight"),
            value:
              (vehicle.permittedTotalWeightKg ?? vehicle.maxWeight) == null
                ? "-"
                : `${vehicle.permittedTotalWeightKg ?? vehicle.maxWeight} kg`,
          },
          {
            title: t("inventory:vehicle_make"),
            value: vehicle.vehicleMake || "-",
          },
          {
            title: t("inventory:vehicle_model"),
            value: vehicle.vehicleModel || "-",
          },
          {
            title: t("inventory:vehicle_type_designation"),
            value: vehicle.vehicleTypeDesignation || "-",
          },
          {
            title: t("inventory:vehicle_vin"),
            value: vehicle.vin || "-",
          },
          {
            title: t("inventory:vehicle_curb_weight"),
            value:
              vehicle.curbWeightKg == null ? "-" : `${vehicle.curbWeightKg} kg`,
          },
          {
            title: t("inventory:vehicle_payload"),
            value:
              vehicle.payloadCapacityKg == null
                ? "-"
                : `${vehicle.payloadCapacityKg} kg`,
          },
          {
            title: t("inventory:vehicle_length"),
            value: vehicle.lengthCm == null ? "-" : `${vehicle.lengthCm} cm`,
          },
          {
            title: t("inventory:vehicle_width"),
            value: vehicle.widthCm == null ? "-" : `${vehicle.widthCm} cm`,
          },
          {
            title: t("inventory:vehicle_height"),
            value: vehicle.heightCm == null ? "-" : `${vehicle.heightCm} cm`,
          },
          {
            title: t("inventory:vehicle_fuel_types"),
            value: vehicle.fuelTypes?.join(", ") || "-",
          },
          {
            title: t("inventory:vehicle_co2"),
            value:
              vehicle.co2EmissionsGKm == null
                ? "-"
                : `${vehicle.co2EmissionsGKm} g/km`,
          },
          {
            title: t("inventory:vehicle_co2_method"),
            value: vehicle.co2MeasurementMethod || "-",
          },
          {
            title: t("inventory:vehicle_fuel_consumption"),
            value:
              vehicle.fuelConsumptionLPer100Km == null
                ? "-"
                : `${vehicle.fuelConsumptionLPer100Km} l/100 km`,
          },
          {
            title: t("inventory:vehicle_electric_consumption"),
            value:
              vehicle.electricEnergyConsumptionWhPerKm == null
                ? "-"
                : `${vehicle.electricEnergyConsumptionWhPerKm} Wh/km`,
          },
          {
            title: t("inventory:vehicle_electric_range"),
            value:
              vehicle.electricRangeKm == null
                ? "-"
                : `${vehicle.electricRangeKm} km`,
          },
          {
            title: t("inventory:vehicle_euro_class"),
            value: vehicle.euroClass || "-",
          },
          {
            title: t("inventory:vehicle_next_inspection"),
            value: vehicle.nextInspectionDate || "-",
          },
          {
            title: t("inventory:vehicle_status"),
            value: vehicle.vehicleStatus || "-",
          },
          {
            title: t("inventory:vehicle_cargo_volume"),
            value:
              vehicle.cargoVolumeM3 == null
                ? "-"
                : `${vehicle.cargoVolumeM3} m³`,
          },
          {
            title: t("inventory:vehicle_pallet_spaces"),
            value: vehicle.palletSpaces ?? "-",
          },
          {
            title: t("inventory:vehicle_special_equipment"),
            value: vehicle.specialEquipment?.join(", ") || "-",
          },
          {
            title: t("vehicles:details.vehicle_type"),
            value: vehicle.categoryCode || "-",
          },
          {
            title: t("vehicles:details.company"),
            value: company?.name,
            redirectPath: `/workspace/companies?company=${company?.name
              .toLowerCase()
              .replace(/\s+/g, "-")}`,
          },
          {
            title: t("vehicles:details.description"),
            value: vehicle.name || "-",
          },
        ]}
      />

      <VehicleDriverAccess
        key={vehicle.id}
        workspaceId={vehicle.workspaceId}
        vehicleId={vehicle.id}
      />
    </>
  );
};
